/*  MUI  */
import { DeleteOutline, SendRounded } from '@mui/icons-material';
import { Box, Button, IconButton, Paper, TableCell } from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { AdminBackButton } from 'application/Shared/Components/Buttons/BackButton';
import { ManagedTable } from 'application/Shared/Components/Table';
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { Helpers, Notify, Response } from 'application/Shared/Helpers';
import { Confirm } from 'application/Shared/Modals';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import i18n from 'i18n';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

/*  Components  */
import Create from './Components/Create';

const headers = [
    {
        renderHeader: i18n.t('Common.event'),
        width: '40%'
    },
    {
        renderHeader: i18n.t('Common.endpoint'),
        width: '45%'
    },
    {
        align: 'right',
        renderHeader: i18n.t('Common.actions'),
        width: '15%'
    }
];

const parameters = { size: -1 };

const searchFields = ['event'];

/*  State  */
const state = () => ({
    clientId: '',
    deleting: false,
    loaded: false,
    rerender: false,
    view: 'list'
});

class Webhooks extends Component {
    constructor(props) {
        super(props);
        this.state = state();
    }

    componentDidMount() {
        const { match } = this.props;
        const { clientId } = match.params;
        this.setState({ clientId }, () => {
            this.load();
        });
    }

    load = () => {
        this.setState({ loaded: true });
    };

    rerender = () => {
        this.setState({ rerender: true }, () =>
            this.setState({ rerender: false })
        );
    };

    delete = async () => {
        const { t } = this.props;
        const { deleting, clientId } = this.state;
        this.setState({ deleting: false });

        /* params */
        const params = {};
        if (clientId) {
            params.platformTenantId = clientId;
        }

        try {
            const response = await axios.delete(`/webhook/${deleting}`, {
                params
            });
            if (!Response.hasResponse(response)) {
                debug.e('Unable to delete Webhook:', response);
                Notify.e(t('Common.error'));
                return;
            }

            Notify.s('Successfully deleted webhook.');
            this.rerender();
        } catch (e) {
            debug.e('Error deleting Webhook', e);
            Notify.e(t('Common.error'));
        }
    };

    test = async (p) => {
        const { clientId } = this.state;
        const { payload } = p;
        Notify.i('Sending Test Payload');

        if (clientId) {
            payload.platformTenantId = clientId;
        }

        try {
            await axios.post('/webhook/test', payload);
            Notify.s('Test sent successfully!');
        } catch (e) {
            debug.e('Webhook Test Error', e);

            if (Helpers.isset(e, 'response', 'data', 'error')) {
                Notify.e(`Webhook Failed '${e.response.data.error}'`, {
                    seconds: 5
                });
                return;
            }

            Notify.e('Webhook Failed');
        }
    };

    renderActions = (row) => {
        const { t } = this.props;
        const { id } = row;

        return (
            <>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => this.test({ payload: row })}
                    data-trackid="admin_utilities_webhooks_test"
                >
                    <SendRounded /> {t('Common.test')}
                </Button>
                <DarkTooltip arrow title={t('Tooltips.Webhooks.delete')}>
                    <IconButton
                        onClick={() => this.setState({ deleting: id })}
                        sx={{ color: 'button.primary' }}
                        data-trackid="admin_utilities_webhooks_delete"
                    >
                        <DeleteOutline />
                    </IconButton>
                </DarkTooltip>
            </>
        );
    };

    renderRow = (row) => {
        const { t } = this.props;
        const { endpoint } = row;

        // TODO: Get event NAME from backend. Right now there's only 1 event
        return (
            <>
                <TableCell>{t('Nightly Digest of Daily App Usage')}</TableCell>
                <TableCell>{endpoint}</TableCell>
                <TableCell align="right">{this.renderActions(row)}</TableCell>
            </>
        );
    };

    renderTable = () => {
        const { t } = this.props;
        const { rerender } = this.state;

        return (
            <Box>
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    width="100%"
                    pt="8px"
                    pr="8px"
                >
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => this.setState({ view: 'create' })}
                    >
                        {t('Webhooks.configure')}
                    </Button>
                </Box>
                <ManagedTable
                    headers={headers}
                    parameters={parameters}
                    renderBody={this.renderRow}
                    renderBodyKey="id"
                    renderFooter
                    rerender={rerender}
                    rowsApi="/webhook"
                    rowsApiPreload
                    rowsPerPage={10}
                    searchEnabled
                    searchFields={searchFields}
                    sortDirection="DESC"
                    sortFieldType="string"
                    sortKey="event"
                />
            </Box>
        );
    };

    renderContent = () => {
        const { view } = this.state;

        switch (view) {
            case 'list':
                return this.renderTable();
            case 'create':
                return (
                    <Create onClose={() => this.setState({ view: 'list' })} />
                );
            default:
                return null;
        }
    };

    renderConfirmDelete = () => {
        const { t } = this.props;
        const { deleting } = this.state;

        return (
            <Confirm
                modalOpen={!!deleting}
                promptConfirmFn={() => this.delete()}
                promptCancelFn={() => {
                    this.setState({ deleting: false });
                }}
                promptTitle={t('Webhooks.confirm_delete')}
                promptText={t('Common.default_confirm_text')}
            />
        );
    };

    render() {
        const { t } = this.props;
        const { loaded, deleting } = this.state;

        return (
            <>
                <PageTitle title={t('Common.webhooks')} />
                {deleting && this.renderConfirmDelete()}
                <AdminBackButton linkTo="/configure" />
                {loaded && (
                    <Paper elevation={0} variant="outlined">
                        {this.renderContent()}
                    </Paper>
                )}
            </>
        );
    }
}

export default withRouter(withTranslation()(Webhooks));
